import { useCallback, useEffect, useState } from "react";
import { Navigate, NavLink, Route, Routes, useParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import {
  getMaintenanceOverview,
  listMaintenanceFailures,
  listMaintenancePolicies,
  listMaintenanceWorkItems,
  requeueMaintenanceFailure,
  runMaintenanceJob,
} from "../../api/clientVendorDirectoryMaintenanceApi";
import ClientVendorDirectorySubnav from "../../components/casamap/admin/ClientVendorDirectorySubnav";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import { formatStatusLabel } from "../../utils/clientPortalFormat";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.maintenancePage";

function translateEnum(t, group, value) {
  if (!value) return "—";
  const key = `${PREFIX}.${group}.${value}`;
  const translated = t(key);
  return translated !== key ? translated : formatStatusLabel(value);
}

function MetricCard({ label, value }) {
  return (
    <div className="cvd-metric-card">
      <div className="cvd-metric-card__label">{label}</div>
      <div className="cvd-metric-card__value">{value ?? "—"}</div>
    </div>
  );
}

function OverviewPage() {
  const { clientViewId } = useParams();
  const { t } = useLocale();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getMaintenanceOverview(clientViewId);
      setOverview(body.overview);
    } catch (error) {
      setErrorMessage(error.message || t(`${PREFIX}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <MaintenanceShell>
      <h1 className="cvd-admin__title">{t(`${PREFIX}.overview.title`)}</h1>
      <p className="lede">{t(`${PREFIX}.overview.intro`)}</p>
      {loading ? <ClientPortalLoading message={t(`${PREFIX}.loading`)} /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}
      {!loading && overview ? (
        <>
          <div className="cvd-metric-grid">
            <MetricCard label={t(`${PREFIX}.kpis.openItems`)} value={overview.queue?.open_items} />
            <MetricCard label={t(`${PREFIX}.kpis.overdueItems`)} value={overview.queue?.overdue_items} />
            <MetricCard label={t(`${PREFIX}.kpis.awaitingReview`)} value={overview.queue?.awaiting_review} />
            <MetricCard label={t(`${PREFIX}.kpis.awaitingVendor`)} value={overview.queue?.awaiting_vendor} />
            <MetricCard label={t(`${PREFIX}.kpis.activePolicies`)} value={overview.activePolicies} />
            <MetricCard label={t(`${PREFIX}.kpis.deadLetters`)} value={overview.openDeadLetters} />
          </div>
          <div className="cvd-panel">
            <h2>{t(`${PREFIX}.overview.scheduledTitle`)}</h2>
            {(overview.scheduledJobs || []).length ? (
              <ul>
                {(overview.scheduledJobs || []).map((job) => (
                  <li key={job}>{translateEnum(t, "jobLabels", job)}</li>
                ))}
              </ul>
            ) : (
              <p className="muted">{t(`${PREFIX}.overview.noJobs`)}</p>
            )}
          </div>
        </>
      ) : null}
    </MaintenanceShell>
  );
}

function WorkQueuePage() {
  const { clientViewId } = useParams();
  const { t } = useLocale();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await listMaintenanceWorkItems(clientViewId, { limit: 100 });
      setItems(body.items || []);
    } catch (error) {
      setErrorMessage(error.message || t(`${PREFIX}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <MaintenanceShell>
      <h1 className="cvd-admin__title">{t(`${PREFIX}.queue.title`)}</h1>
      <p className="lede">{t(`${PREFIX}.queue.intro`)}</p>
      {loading ? <ClientPortalLoading message={t(`${PREFIX}.queue.loading`)} /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}
      {!loading ? (
        items.length ? (
          <table className="cvd-table">
            <caption className="sr-only">{t(`${PREFIX}.queue.caption`)}</caption>
            <thead>
              <tr>
                <th scope="col">{t(`${PREFIX}.table.policy`)}</th>
                <th scope="col">{t(`${PREFIX}.table.action`)}</th>
                <th scope="col">{t(`${PREFIX}.table.risk`)}</th>
                <th scope="col">{t(`${PREFIX}.table.status`)}</th>
                <th scope="col">{t(`${PREFIX}.table.reason`)}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{translateEnum(t, "policies", item.policyKey)}</td>
                  <td>{translateEnum(t, "actions", item.actionType)}</td>
                  <td>{translateEnum(t, "risk", item.riskLevel)}</td>
                  <td>{translateEnum(t, "status", item.status)}</td>
                  <td>{item.reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">{t(`${PREFIX}.queue.empty`)}</p>
        )
      ) : null}
    </MaintenanceShell>
  );
}

function PoliciesPage() {
  const { clientViewId } = useParams();
  const { t } = useLocale();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await listMaintenancePolicies(clientViewId);
      setPolicies(body.policies || []);
    } catch (error) {
      setErrorMessage(error.message || t(`${PREFIX}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <MaintenanceShell>
      <h1 className="cvd-admin__title">{t(`${PREFIX}.policies.title`)}</h1>
      <p className="lede">{t(`${PREFIX}.policies.intro`)}</p>
      {loading ? <ClientPortalLoading message={t(`${PREFIX}.policies.loading`)} /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}
      {!loading ? (
        policies.length ? (
          <table className="cvd-table">
            <caption className="sr-only">{t(`${PREFIX}.policies.caption`)}</caption>
            <thead>
              <tr>
                <th scope="col">{t(`${PREFIX}.table.policy`)}</th>
                <th scope="col">{t(`${PREFIX}.table.version`)}</th>
                <th scope="col">{t(`${PREFIX}.table.status`)}</th>
                <th scope="col">{t(`${PREFIX}.table.action`)}</th>
                <th scope="col">{t(`${PREFIX}.table.risk`)}</th>
              </tr>
            </thead>
            <tbody>
              {policies.map((policy) => (
                <tr key={`${policy.policyKey}-${policy.version}`}>
                  <td>{translateEnum(t, "policies", policy.policyKey)}</td>
                  <td>{policy.version}</td>
                  <td>{translateEnum(t, "status", policy.status)}</td>
                  <td>{translateEnum(t, "actions", policy.actionType)}</td>
                  <td>{translateEnum(t, "risk", policy.riskLevel)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">{t(`${PREFIX}.policies.empty`)}</p>
        )
      ) : null}
    </MaintenanceShell>
  );
}

function FailuresPage() {
  const { clientViewId } = useParams();
  const { t } = useLocale();
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [actionMessage, setActionMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await listMaintenanceFailures(clientViewId);
      setFailures(body.failures || []);
    } catch (error) {
      setErrorMessage(error.message || t(`${PREFIX}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, t]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleRequeue(deadLetterId) {
    setActionMessage("");
    try {
      await requeueMaintenanceFailure(clientViewId, deadLetterId);
      setActionMessage(t(`${PREFIX}.failures.requeued`));
      await load();
    } catch (error) {
      setActionMessage(error.message || t(`${PREFIX}.failures.requeueError`));
    }
  }

  return (
    <MaintenanceShell>
      <h1 className="cvd-admin__title">{t(`${PREFIX}.failures.title`)}</h1>
      <p className="lede">{t(`${PREFIX}.failures.intro`)}</p>
      {loading ? <ClientPortalLoading message={t(`${PREFIX}.failures.loading`)} /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}
      {actionMessage ? <p role="status">{actionMessage}</p> : null}
      {!loading ? (
        failures.length ? (
          <table className="cvd-table">
            <caption className="sr-only">{t(`${PREFIX}.failures.caption`)}</caption>
            <thead>
              <tr>
                <th scope="col">{t(`${PREFIX}.table.policy`)}</th>
                <th scope="col">{t(`${PREFIX}.table.resource`)}</th>
                <th scope="col">{t(`${PREFIX}.table.category`)}</th>
                <th scope="col">{t(`${PREFIX}.table.error`)}</th>
                <th scope="col">{t(`${PREFIX}.table.actions`)}</th>
              </tr>
            </thead>
            <tbody>
              {failures.map((item) => (
                <tr key={item.id}>
                  <td>{translateEnum(t, "policies", item.policy_key)}</td>
                  <td>{translateEnum(t, "resources", item.resource_type)}</td>
                  <td>{translateEnum(t, "failureCategory", item.failure_category)}</td>
                  <td>{item.last_error || "—"}</td>
                  <td>
                    <button type="button" className="cvd-btn cvd-btn--secondary" onClick={() => handleRequeue(item.id)}>
                      {t(`${PREFIX}.failures.retry`)}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">{t(`${PREFIX}.failures.empty`)}</p>
        )
      ) : null}
    </MaintenanceShell>
  );
}

function JobsPage() {
  const { clientViewId } = useParams();
  const { t } = useLocale();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [runMessage, setRunMessage] = useState("");
  const [runningJob, setRunningJob] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getMaintenanceOverview(clientViewId);
      setOverview(body.overview);
    } catch (error) {
      setErrorMessage(error.message || t(`${PREFIX}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, t]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleRun(jobKey, dryRun = true) {
    setRunningJob(jobKey);
    setRunMessage("");
    try {
      await runMaintenanceJob(clientViewId, jobKey, { dryRun });
      setRunMessage(
          t(dryRun ? `${PREFIX}.jobsPage.previewDone` : `${PREFIX}.jobsPage.runDone`, {
          job: translateEnum(t, "jobLabels", jobKey),
        })
      );
      const body = await getMaintenanceOverview(clientViewId);
      setOverview(body.overview);
    } catch (error) {
      setRunMessage(error.message || t(`${PREFIX}.jobsPage.runError`));
    } finally {
      setRunningJob(null);
    }
  }

  return (
    <MaintenanceShell>
      <h1 className="cvd-admin__title">{t(`${PREFIX}.jobsPage.title`)}</h1>
      <p className="lede">{t(`${PREFIX}.jobsPage.intro`)}</p>
      {loading ? <ClientPortalLoading message={t(`${PREFIX}.jobsPage.loading`)} /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}
      {runMessage ? <p role="status">{runMessage}</p> : null}
      {!loading && overview ? (
        (overview.scheduledJobs || []).length ? (
          <ul className="cvd-list">
            {(overview.scheduledJobs || []).map((jobKey) => (
              <li key={jobKey} className="cvd-list__item">
                <span>{translateEnum(t, "jobLabels", jobKey)}</span>
                <div className="cvd-list__actions">
                  <button
                    type="button"
                    className="cvd-btn cvd-btn--secondary"
                    disabled={runningJob === jobKey}
                    onClick={() => handleRun(jobKey, true)}
                  >
                    {t(`${PREFIX}.jobsPage.preview`)}
                  </button>
                  <button
                    type="button"
                    className="cvd-btn cvd-btn--primary"
                    disabled={runningJob === jobKey}
                    onClick={() => handleRun(jobKey, false)}
                  >
                    {t(`${PREFIX}.jobsPage.run`)}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">{t(`${PREFIX}.overview.noJobs`)}</p>
        )
      ) : null}
    </MaintenanceShell>
  );
}

function MaintenanceShell({ children }) {
  const { t } = useLocale();
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const base = `${serviciosBase}/mantenimiento`;

  return (
    <div className="page-content cvd-admin">
      <ClientVendorDirectorySubnav clientViewId={clientViewId} />
      <nav className="cvd-tabs" aria-label={t(`${PREFIX}.navLabel`)}>
        <NavLink to={base} end className={({ isActive }) => `cvd-tab${isActive ? " cvd-tab--active" : ""}`}>
          {t(`${PREFIX}.nav.overview`)}
        </NavLink>
        <NavLink to={`${base}/tareas`} className={({ isActive }) => `cvd-tab${isActive ? " cvd-tab--active" : ""}`}>
          {t(`${PREFIX}.nav.queue`)}
        </NavLink>
        <NavLink to={`${base}/politicas`} className={({ isActive }) => `cvd-tab${isActive ? " cvd-tab--active" : ""}`}>
          {t(`${PREFIX}.nav.policies`)}
        </NavLink>
        <NavLink to={`${base}/trabajos`} className={({ isActive }) => `cvd-tab${isActive ? " cvd-tab--active" : ""}`}>
          {t(`${PREFIX}.nav.jobs`)}
        </NavLink>
        <NavLink to={`${base}/fallos`} className={({ isActive }) => `cvd-tab${isActive ? " cvd-tab--active" : ""}`}>
          {t(`${PREFIX}.nav.failures`)}
        </NavLink>
      </nav>
      {children}
    </div>
  );
}

export default function ClientVendorDirectoryMaintenanceHubPage() {
  const { serviciosBase } = useVendorDirectoryPaths();
  const base = `${serviciosBase}/mantenimiento`;

  return (
    <Routes>
      <Route index element={<OverviewPage />} />
      <Route path="tareas" element={<WorkQueuePage />} />
      <Route path="politicas" element={<PoliciesPage />} />
      <Route path="trabajos" element={<JobsPage />} />
      <Route path="fallos" element={<FailuresPage />} />
      <Route path="*" element={<Navigate to={base} replace />} />
    </Routes>
  );
}
